import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type { OptionType } from '@/api/common';
import { ComboboxPickerMultiple, ComboboxPickerSingle } from '@/components/Form/pickers';
import type { FilterOperatorMulti, FilterOperatorValue } from '@/components/InstantSearch';

import { IncludeExcludeToggle } from '../IncludeExcludeToggle';

type Props<T extends OptionType> = {
  value?: FilterOperatorMulti | null;
  items?: T[];
  filteredItems?: T[];
  isLoading?: boolean;
  error?: Error | null;
  mode?: 'multiple' | 'single';
  hasExclude?: boolean;
  singlePlaceholder: string;
  multiplePlaceholder: string;
  multipleLoadingPlaceholder?: string;
  notFoundContent: ReactNode;
  renderOption?: (option: T) => ReactNode;
  onChange?: (value: FilterOperatorValue) => void;
};

const ComboboxPickerFilter = <T extends OptionType>({
  value,
  items,
  filteredItems,
  isLoading = false,
  error,
  mode = 'multiple',
  hasExclude = true,
  singlePlaceholder,
  multiplePlaceholder,
  multipleLoadingPlaceholder,
  notFoundContent,
  renderOption,
  onChange,
}: Props<T>) => {
  if (value?.operator && value.operator !== 'in' && value.operator !== 'notIn') {
    throw new Error(`Invalid operator: ${value.operator}`);
  }

  const operator = value?.operator ?? 'in';

  const optionsById = useMemo(
    () => new Map((items ?? filteredItems ?? []).map((option) => [option.id, option])),
    [items, filteredItems],
  );

  const selectedOptions = useMemo<T[]>(() => {
    const result: T[] = [];
    for (const id of value?.operand ?? []) {
      const option = optionsById.get(String(id));
      if (option) {
        result.push(option);
      }
    }
    return result;
  }, [optionsById, value?.operand]);

  return (
    <div className="flex flex-col gap-2">
      {hasExclude && (
        <IncludeExcludeToggle
          operator={operator}
          onChange={(newOperator) => {
            onChange?.({
              operator: newOperator,
              operand: value?.operand ?? [],
            });
          }}
        />
      )}
      {mode === 'single' ? (
        <ComboboxPickerSingle
          items={items}
          filteredItems={filteredItems}
          value={selectedOptions[0] ?? null}
          isLoading={isLoading}
          error={error}
          placeholder={singlePlaceholder}
          notFoundContent={notFoundContent}
          renderOption={renderOption}
          onChange={(selectedValue) => {
            if (!selectedValue) {
              onChange?.(null);
              return;
            }

            onChange?.({
              operator,
              operand: [selectedValue.id],
            });
          }}
        />
      ) : (
        <ComboboxPickerMultiple<T>
          items={items}
          filteredItems={filteredItems}
          value={selectedOptions}
          isLoading={isLoading}
          error={error}
          placeholder={multiplePlaceholder}
          loadingPlaceholder={multipleLoadingPlaceholder}
          notFoundContent={notFoundContent}
          renderOption={renderOption}
          onChange={(selectedValue) => {
            if (!selectedValue?.length) {
              onChange?.(null);
              return;
            }

            onChange?.({
              operator,
              operand: selectedValue.map((option) => option.id),
            });
          }}
        />
      )}
    </div>
  );
};

export default ComboboxPickerFilter;
